import { useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Shield, Users, User as UserIcon, Plus, X, ChevronDown, ChevronRight, Settings, Save, Loader2 } from 'lucide-react';
import { api, registerDirtySource } from '@onvice/portal-shell-api';
import { BackLink, getModuleLabel, useDialogs, usePageLabel } from '@onvice/portal-ui';
import ModulePermissionsMatrix from '../../components/ModulePermissionsMatrix';
import { AuditActionCell } from '../../lib/auditText';

type Level = 'admin' | 'editor' | 'author' | 'tester' | 'reader' | 'depositor' | 'none';

const LEVELS: Level[] = ['none', 'reader', 'tester', 'author', 'editor', 'admin'];
const LEVEL_TEXT: Record<Level, string> = {
  none: 'kein Zugriff', reader: 'Leser', tester: 'Tester', author: 'Autor', editor: 'Editor', admin: 'Admin', depositor: 'Ablage',
};
const LEVEL_RANK: Record<Level, number> = { none: 0, depositor: 1, reader: 2, tester: 3, author: 4, editor: 5, admin: 6 };

const LEVEL_COLOR: Record<Level, string> = {
  admin:    'bg-brand-500/20 text-brand-300 border-brand-500/30',
  editor:   'bg-sky-500/20 text-sky-300 border-sky-500/30',
  author:   'bg-blue-500/20 text-blue-300 border-blue-500/30',
  tester:   'bg-purple-500/20 text-purple-300 border-purple-500/30',
  reader:   'bg-slate-500/20 text-slate-300 border-slate-500/30',
  depositor:'bg-amber-500/20 text-amber-300 border-amber-500/30',
  none:     'bg-slate-800 text-slate-500 border-slate-700',
};

interface ModuleDetail {
  module_slug: string;
  default_level: Level;
  persons: Array<{ subject_id: string; level: Level; first_name: string; last_name: string; email: string }>;
  groups: Array<{ subject_id: string; level: Level; name: string }>;
}

interface PersonOpt { id: string; first_name: string; last_name: string; email: string }
interface GroupOpt { id: string; name: string }

type PresetLevel = 'reader' | 'tester' | 'author' | 'editor' | 'admin';
type FeinKey = string; // "default" | "person:<id>" | "group:<id>"
/** Schlüssel einer gesammelten Änderung: "default" | "person:<id>" | "group:<id>" */
type ChangeKey = string;

/** Joerg 2026-09-08: modulspezifische Einstellungsseiten sind ueber die
 *  Modul-Verwaltung erreichbar. Sobald ein Modul eine eigene Settings-Route
 *  bekommt, hier den Eintrag ergaenzen — die Detailseite zeigt dann den
 *  „Modul-Einstellungen"-Link. */
const MODULE_SETTINGS_ROUTES: Record<string, string> = {
  // V2-Pfad — die V1-Settings-Seite (/m/bauberichte/settings) ist ueber die
  // Modul-ACL nicht mehr erreichbar (Fund Joerg 2026-09-08: Redirect auf /).
  bauberichte: '/m/bauberichteV2/einstellungen',
  bauberichteV2: '/m/bauberichteV2/einstellungen',
  // Joerg 2026-09-10: Wetterdienst-Schalter (Schlechtwetter-Anzeige).
  plantafel: '/m/plantafel/einstellungen',
};

/**
 * Modul-Detail (Rechte pro Modul). Dokument-Navigation 1.44 (Jörg 2026-09-29, R5):
 * Level-Änderungen, neue Einträge und Entfernen werden gesammelt und mit „Speichern (n)"
 * geschrieben — vorher schrieb jede Auswahl sofort. Die Fein-Rechte-Matrix hatte das
 * Muster schon (ModulePermissionsMatrix).
 */
export default function AdminAdressbuchModuleDetailPage() {
  const { slug } = useParams();
  const dlg = useDialogs();
  const [detail, setDetail] = useState<ModuleDetail | null>(null);
  const [allPersons, setAllPersons] = useState<PersonOpt[]>([]);
  const [allGroups, setAllGroups] = useState<GroupOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Gesammelte Änderungen: Ziel-Level je Subjekt (none = Eintrag entfernen).
  const [pending, setPending] = useState<Map<ChangeKey, Level>>(new Map());
  interface AuditRow { id: string; actor_full_name: string; action: string; details: Record<string, unknown>; created_at: string }
  const [audit, setAudit] = useState<AuditRow[] | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);
  // Klartext-Bezeichnungen der Rechte für ältere Protokolleinträge ohne eigene Beschriftung.
  const [permLabels, setPermLabels] = useState<Record<string, string>>({});
  function loadAudit() {
    if (!slug) return;
    setAuditError(null);
    api.get<{ success: boolean; data: AuditRow[] }>(`/admin/acl/module-permissions/audit?module=${encodeURIComponent(slug)}&limit=50`)
      .then((r) => setAudit(r.data ?? []))
      .catch((err: unknown) => { setAudit([]); setAuditError((err as Error).message || 'Protokoll konnte nicht geladen werden'); });
    api.get<{ success: boolean; data: { catalog: Array<{ slug: string; label: string }> } }>(`/admin/acl/module-permissions?module=${encodeURIComponent(slug)}&subject_type=default`)
      .then((r) => setPermLabels(Object.fromEntries((r.data.catalog ?? []).map((c) => [c.slug, c.label]))))
      .catch(() => { /* ohne Katalog bleiben die technischen Bezeichnungen */ });
  }
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  // Fein-ACL (Joerg 2026-08-20): pro (Modul, Subject) kann die Haekchen-Matrix
  // aufgeklappt werden — direkt unter der Level-Zeile erscheint der Editor.
  const [feinOpen, setFeinOpen] = useState<Set<FeinKey>>(new Set());
  const toggleFein = (key: FeinKey) => setFeinOpen((prev) => {
    const s = new Set(prev);
    if (s.has(key)) s.delete(key); else s.add(key);
    return s;
  });
  const isPresetLevel = (l: Level): l is PresetLevel =>
    l === 'reader' || l === 'tester' || l === 'author' || l === 'editor' || l === 'admin';
  usePageLabel(detail ? getModuleLabel(detail.module_slug) : null, 'document');

  useEffect(() => registerDirtySource({
    id: `modul:${slug ?? ''}:rechte`,
    label: 'Modul-Rechte',
    isDirty: () => pendingRef.current.size > 0,
    discard: () => setPending(new Map()),
  }), [slug]);

  function load() {
    if (!slug) return;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<{ success: boolean; data: ModuleDetail }>(`/admin/adressbuch/modules/${slug}`),
      api.get<{ success: boolean; data: PersonOpt[] }>(`/admin/adressbuch/persons`),
      api.get<{ success: boolean; data: GroupOpt[] }>(`/admin/adressbuch/groups`),
    ])
      .then(([m, p, g]) => { setDetail(m.data); setAllPersons(p.data); setAllGroups(g.data); })
      .catch((err: unknown) => setError((err as Error).message ?? 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  }
  useEffect(load, [slug]);
  useEffect(loadAudit, [slug]);

  const savedLevel = useMemo(() => {
    const m = new Map<ChangeKey, Level>();
    if (!detail) return m;
    m.set('default', detail.default_level);
    for (const g of detail.groups) m.set(`group:${g.subject_id}`, g.level);
    for (const p of detail.persons) m.set(`person:${p.subject_id}`, p.level);
    return m;
  }, [detail]);

  const setLevel = (key: ChangeKey, level: Level) => setPending((prev) => {
    const m = new Map(prev);
    if (level === (savedLevel.get(key) ?? 'none')) m.delete(key); else m.set(key, level);
    return m;
  });
  const effective = (key: ChangeKey): Level => pending.get(key) ?? savedLevel.get(key) ?? 'none';

  async function saveAll() {
    if (!slug || pending.size === 0) return;
    // Rückfrage vor dem Schreiben (Jörg 2026-10-01, Dokument-Navigation 2.02): Das Level ist
    // dasselbe Preset wie in der Rechte-Matrix — wer eine Gruppe oder den Standard hochstuft,
    // sieht vorher, was sich ändert. Geschrieben wird erst nach „Speichern".
    const lines = [...pending.entries()].map(([key, level]) => {
      const from = savedLevel.get(key) ?? 'none';
      return `${labelFor(key)}: ${LEVEL_TEXT[from]} → ${LEVEL_TEXT[level]}`;
    });
    const raisesBroadly = [...pending.entries()].some(([key, level]) =>
      (key === 'default' || key.startsWith('group:')) && LEVEL_RANK[level] > LEVEL_RANK[savedLevel.get(key) ?? 'none']);
    const ok = await dlg.confirm({
      title: `Zugriffsrechte speichern (${pending.size})?`,
      message: `${lines.join(' · ')}. ${raisesBroadly ? 'Mindestens eine Gruppe oder der Standard bekommt mehr Rechte — das gilt sofort für alle Mitglieder. ' : ''}Jede Änderung wird im Protokoll festgehalten.`,
      confirmLabel: 'Speichern',
      cancelLabel: 'Abbrechen',
      danger: raisesBroadly,
    });
    if (!ok) return;
    setSaving(true);
    const failed: string[] = [];
    const done: ChangeKey[] = [];
    for (const [key, level] of pending) {
      try {
        if (key === 'default') {
          await api.put(`/admin/adressbuch/modules/${slug}/default`, { level });
        } else {
          const [subjectType, subjectId] = key.split(':') as ['person' | 'group', string];
          await api.put(`/admin/adressbuch/modules/${slug}/access/${subjectType}/${subjectId}`, { level });
        }
        done.push(key);
      } catch (e) {
        failed.push(`${labelFor(key)}: ${(e as Error).message || 'Speichern fehlgeschlagen'}`);
      }
    }
    setPending((prev) => { const m = new Map(prev); for (const k of done) m.delete(k); return m; });
    setSaving(false);
    load();
    loadAudit();
    if (failed.length > 0) {
      await dlg.notify({ kind: 'error', title: 'Modul-Rechte teilweise nicht gespeichert', message: 'Diese Einträge bleiben zum erneuten Speichern markiert:', items: failed });
    }
  }

  function labelFor(key: ChangeKey): string {
    if (key === 'default') return 'Standard';
    const [type, id] = key.split(':');
    if (type === 'group') return `Gruppe ${allGroups.find((g) => g.id === id)?.name ?? detail?.groups.find((g) => g.subject_id === id)?.name ?? ''}`.trim();
    const p = allPersons.find((x) => x.id === id) ?? (() => { const d = detail?.persons.find((x) => x.subject_id === id); return d ? { first_name: d.first_name, last_name: d.last_name } : null; })();
    return p ? `${p.first_name} ${p.last_name}` : 'Person';
  }

  if (loading && !detail) return <div className="px-4 sm:px-8 py-6 text-slate-400">Laden…</div>;
  if (error) {
    return (
      <div className="px-4 sm:px-8 py-6 space-y-3">
        <BackLink variant="text" fallback="/admin/adressbuch?tab=modules" fallbackLabel="Adressbuch" />
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-300 flex flex-wrap items-center justify-between gap-2" data-test-anchor="ladefehler">
          <span>Nicht geladen: {error}</span>
          <button type="button" onClick={load} className="px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs">Erneut laden</button>
        </div>
      </div>
    );
  }
  if (!detail) return null;

  // Gespeicherte Einträge + gesammelt hinzugefügte (noch nicht gespeicherte) Zeilen.
  const savedGroupIds = new Set(detail.groups.map((g) => g.subject_id));
  const savedPersonIds = new Set(detail.persons.map((p) => p.subject_id));
  const addedGroups = [...pending.keys()].filter((k) => k.startsWith('group:') && !savedGroupIds.has(k.slice(6))).map((k) => k.slice(6));
  const addedPersons = [...pending.keys()].filter((k) => k.startsWith('person:') && !savedPersonIds.has(k.slice(7))).map((k) => k.slice(7));
  const groupsToAdd = allGroups.filter((g) => !savedGroupIds.has(g.id) && !addedGroups.includes(g.id));
  const personsToAdd = allPersons.filter((p) => !savedPersonIds.has(p.id) && !addedPersons.includes(p.id));

  const saveBar = (
    <div className="flex flex-wrap items-center justify-end gap-2" data-test-anchor="modul-speichern" data-dirty={pending.size > 0 ? '1' : '0'}>
      {pending.size > 0 && (
        <button type="button" disabled={saving} onClick={() => setPending(new Map())}
                className="px-3 py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm min-h-[44px] disabled:opacity-50">
          Verwerfen ({pending.size})
        </button>
      )}
      <button type="button" disabled={saving || pending.size === 0} onClick={() => { void saveAll(); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-primary-foreground text-sm min-h-[44px]">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        {saving ? 'Speichern…' : pending.size === 0 ? 'Keine Änderungen' : `Speichern (${pending.size})`}
      </button>
    </div>
  );

  const rowClass = (changed: boolean) => `flex items-center gap-2 px-3 py-2 rounded border ${changed ? 'border-amber-400' : 'border-default'}`;

  return (
    <div className="px-4 sm:px-8 py-6 max-w-5xl mx-auto space-y-6" data-test-anchor="module-page" data-dirty={pending.size > 0 ? '1' : '0'}>
      <div className="flex items-center gap-3 flex-wrap">
        <BackLink fallback="/admin/adressbuch?tab=modules" fallbackLabel="Adressbuch" />
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Shield size={20} />
          <span>{getModuleLabel(detail.module_slug)}</span>
          <span className="font-mono text-sm text-muted-foreground">{detail.module_slug}</span>
        </h1>
        <div className="ml-auto">{saveBar}</div>
      </div>
      <p className="text-[11px] text-slate-500 -mt-3">
        Level-Änderungen, neue Einträge und Entfernen gelten erst nach „Speichern". Die Fein-Rechte-Matrix speichert für sich mit ihrem eigenen Knopf.
      </p>

      {/* Modul-Einstellungen (Joerg 2026-09-08): Einstieg zur fachlichen
          Einstellungsseite des Moduls, wenn es eine hat. */}
      {MODULE_SETTINGS_ROUTES[detail.module_slug] && (
        <section className="bg-card border border-default rounded-lg p-4">
          <h2 className="text-sm font-semibold text-slate-300 mb-2 flex items-center gap-2">
            <Settings size={15} /> Modul-Einstellungen
          </h2>
          <p className="text-[11px] text-slate-500 mb-3">
            Fachliche Optionen dieses Moduls (unabhängig von den Zugriffsrechten unten).
          </p>
          <Link
            to={MODULE_SETTINGS_ROUTES[detail.module_slug]!}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-sm min-h-[40px]"
          >
            <Settings size={15} /> Einstellungen öffnen
          </Link>
        </section>
      )}

      {/* Default */}
      <section className="bg-card border border-default rounded-lg p-4" data-test-anchor="modul-standard">
        <h2 className="text-sm font-semibold text-slate-300 mb-2">Standard</h2>
        <p className="text-[11px] text-slate-500 mb-3">
          Greift für alle Personen, die weder direkt noch über eine Gruppe einen Eintrag haben.
        </p>
        <div className="flex items-center gap-2">
          <LevelDropdown value={effective('default')} disabled={saving} changed={pending.has('default')} onChange={(l) => setLevel('default', l)} />
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${LEVEL_COLOR[effective('default')]}`}>
            {effective('default')}
          </span>
          <FeinToggleButton open={feinOpen.has('default')} onClick={() => toggleFein('default')} />
        </div>
        {feinOpen.has('default') && (
          <div className="mt-3">
            <ModulePermissionsMatrix
              moduleSlug={detail.module_slug}
              subjectType="default"
              subjectId={null}
              subjectLabel="den Standard (alle ohne eigenen Eintrag)"
              currentLevel={isPresetLevel(detail.default_level) ? detail.default_level : undefined}
              onChanged={() => { load(); loadAudit(); }}
            />
          </div>
        )}
      </section>

      {/* Gruppen */}
      <section className="bg-card border border-default rounded-lg p-4" data-test-anchor="modul-gruppen">
        <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Users size={14} /> Gruppen mit Eintrag
        </h2>
        {detail.groups.length === 0 && addedGroups.length === 0 ? (
          <p className="text-xs text-slate-500 italic mb-3">Keine Gruppen-Einträge</p>
        ) : (
          <div className="space-y-1 mb-3">
            {detail.groups.map((g) => {
              const key = `group:${g.subject_id}`;
              const open = feinOpen.has(key);
              const level = effective(key);
              return (
                <Fragment key={g.subject_id}>
                  <div className={rowClass(pending.has(key))}>
                    <span className="text-sm flex-1">{g.name}{level === 'none' && <span className="ml-2 text-[11px] text-rose-300">wird entfernt</span>}</span>
                    <LevelDropdown value={level} disabled={saving} changed={pending.has(key)} onChange={(l) => setLevel(key, l)} />
                    <FeinToggleButton open={open} onClick={() => toggleFein(key)} />
                    <button
                      type="button"
                      onClick={() => setLevel(key, 'none')}
                      disabled={saving}
                      title="Eintrag entfernen (gilt nach Speichern)"
                      className="p-1 text-slate-500 hover:text-red-400 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {open && (
                    <div className="px-3">
                      <ModulePermissionsMatrix
                        moduleSlug={detail.module_slug}
                        subjectType="group"
                        subjectId={g.subject_id}
                        subjectLabel={`Gruppe „${g.name}“`}
                        currentLevel={isPresetLevel(g.level) ? g.level : undefined}
                        onChanged={() => { load(); loadAudit(); }}
                      />
                    </div>
                  )}
                </Fragment>
              );
            })}
            {addedGroups.map((gid) => {
              const key = `group:${gid}`;
              return (
                <div key={key} className={rowClass(true)}>
                  <span className="text-sm flex-1">{allGroups.find((g) => g.id === gid)?.name ?? 'Gruppe'} <span className="ml-2 text-[11px] text-amber-300">neu</span></span>
                  <LevelDropdown value={effective(key)} disabled={saving} changed onChange={(l) => setLevel(key, l)} />
                  <button type="button" onClick={() => setLevel(key, 'none')} disabled={saving} title="Wieder entfernen"
                          className="p-1 text-slate-500 hover:text-red-400 min-h-[44px] min-w-[44px] flex items-center justify-center">
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {groupsToAdd.length > 0 && (
          <AddRow
            label="Gruppe hinzufügen"
            options={groupsToAdd.map((g) => ({ value: g.id, label: g.name }))}
            onAdd={(id, level) => setLevel(`group:${id}`, level)}
            disabled={saving}
          />
        )}
      </section>

      {/* Personen */}
      <section className="bg-card border border-default rounded-lg p-4" data-test-anchor="modul-personen">
        <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <UserIcon size={14} /> Personen mit Direct-Eintrag
        </h2>
        {detail.persons.length === 0 && addedPersons.length === 0 ? (
          <p className="text-xs text-slate-500 italic mb-3">Keine Direct-Person-Einträge</p>
        ) : (
          <div className="space-y-1 mb-3">
            {detail.persons.map((p) => {
              const key = `person:${p.subject_id}`;
              const open = feinOpen.has(key);
              const level = effective(key);
              return (
                <Fragment key={p.subject_id}>
                  <div className={rowClass(pending.has(key))}>
                    <span className="text-sm flex-1">
                      <Link to={`/admin/adressbuch/persons/${p.subject_id}`} className="hover:text-brand-400">
                        {p.first_name} {p.last_name}
                      </Link>
                      <span className="text-[11px] text-slate-500 ml-2">{p.email}</span>
                      {level === 'none' && <span className="ml-2 text-[11px] text-rose-300">wird entfernt</span>}
                    </span>
                    <LevelDropdown value={level} disabled={saving} changed={pending.has(key)} onChange={(l) => setLevel(key, l)} />
                    <FeinToggleButton open={open} onClick={() => toggleFein(key)} />
                    <button
                      type="button"
                      onClick={() => setLevel(key, 'none')}
                      disabled={saving}
                      title="Eintrag entfernen (gilt nach Speichern)"
                      className="p-1 text-slate-500 hover:text-red-400 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      <X size={14} />
                    </button>
                  </div>
                  {open && (
                    <div className="px-3">
                      <ModulePermissionsMatrix
                        moduleSlug={detail.module_slug}
                        subjectType="person"
                        subjectId={p.subject_id}
                        subjectLabel={`${p.first_name} ${p.last_name}`.trim()}
                        currentLevel={isPresetLevel(p.level) ? p.level : undefined}
                        onChanged={() => { load(); loadAudit(); }}
                      />
                    </div>
                  )}
                </Fragment>
              );
            })}
            {addedPersons.map((pid) => {
              const key = `person:${pid}`;
              const p = allPersons.find((x) => x.id === pid);
              return (
                <div key={key} className={rowClass(true)}>
                  <span className="text-sm flex-1">{p ? `${p.first_name} ${p.last_name}` : 'Person'} <span className="ml-2 text-[11px] text-amber-300">neu</span></span>
                  <LevelDropdown value={effective(key)} disabled={saving} changed onChange={(l) => setLevel(key, l)} />
                  <button type="button" onClick={() => setLevel(key, 'none')} disabled={saving} title="Wieder entfernen"
                          className="p-1 text-slate-500 hover:text-red-400 min-h-[44px] min-w-[44px] flex items-center justify-center">
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {personsToAdd.length > 0 && (
          <AddRow
            label="Person hinzufügen"
            options={personsToAdd.map((p) => ({ value: p.id, label: `${p.first_name} ${p.last_name} (${p.email})` }))}
            onAdd={(id, level) => setLevel(`person:${id}`, level)}
            disabled={saving}
          />
        )}
      </section>

      {/* Protokoll (Jörg 2026-10-01: was genau wurde geändert) */}
      <section className="bg-card border border-default rounded-lg p-4" data-test-anchor="modul-protokoll">
        <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center justify-between gap-2">
          <span>Protokoll der Rechteänderungen (letzte 50)</span>
          <button type="button" onClick={loadAudit} className="text-xs text-slate-400 hover:text-slate-200">Neu laden</button>
        </h2>
        {auditError && <p className="text-xs text-rose-300 mb-2">{auditError}</p>}
        {audit == null
          ? <p className="text-xs text-slate-500">Laden…</p>
          : audit.length === 0
            ? <p className="text-xs text-slate-500 italic">Noch keine Einträge.</p>
            : (
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase">
                  <tr>
                    <th className="text-left py-1 pr-3">Zeit</th>
                    <th className="text-left py-1 pr-3">Von</th>
                    <th className="text-left py-1">Änderung</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.map((e) => (
                    <tr key={e.id} className="border-t border-slate-800 align-top">
                      <td className="py-1 pr-3 text-slate-400 whitespace-nowrap">{new Date(e.created_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td className="py-1 pr-3 text-slate-200 whitespace-nowrap">{e.actor_full_name}</td>
                      <td className="py-1 text-slate-300"><AuditActionCell entry={e} labels={permLabels} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
      </section>

      {saveBar}
    </div>
  );
}

function FeinToggleButton({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Fein-Rechte-Matrix ein-/ausklappen"
      className={`flex items-center gap-1 rounded border px-2 h-8 text-[11px] transition ${
        open
          ? 'border-brand-500 bg-brand-500/20 text-brand-200'
          : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500'
      }`}
    >
      {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      Rechte
    </button>
  );
}

function LevelDropdown({ value, onChange, disabled, changed = false }: { value: Level; onChange: (l: Level) => void; disabled?: boolean; changed?: boolean }) {
  return (
    <select
      value={value}
      disabled={disabled}
      data-changed={changed ? '1' : '0'}
      onChange={(e) => onChange(e.target.value as Level)}
      className={`h-8 px-2 rounded bg-slate-800 border text-slate-100 text-[12px] ${changed ? 'border-amber-400 ring-2 ring-amber-400/40' : 'border-slate-700'}`}
    >
      {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
    </select>
  );
}

function AddRow({ label, options, onAdd, disabled }: {
  label: string;
  options: Array<{ value: string; label: string }>;
  onAdd: (id: string, level: Level) => void;
  disabled?: boolean;
}) {
  const [sel, setSel] = useState<string>('');
  const [level, setLevel] = useState<Level>('reader');
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded border border-dashed border-default">
      <select
        value={sel}
        onChange={(e) => setSel(e.target.value)}
        disabled={disabled}
        className="flex-1 h-8 px-2 rounded bg-slate-800 border border-slate-700 text-slate-100 text-[12px]"
      >
        <option value="">{label}…</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <LevelDropdown value={level} disabled={disabled} onChange={setLevel} />
      <button
        onClick={() => { if (sel) { onAdd(sel, level); setSel(''); } }}
        disabled={disabled || !sel}
        className="flex items-center gap-1 px-2 py-1 rounded bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-primary-foreground text-[12px] min-h-[36px]"
      >
        <Plus size={12} /> Vormerken
      </button>
    </div>
  );
}
